import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import { Link } from "react-router-dom";
import { useTranslation } from 'react-i18next';
import { localizeSystemText } from '../../i18n/messages';
import { displayDate, displayDateTime } from "../common/datePresentation";
import { formatLedgerAmount } from "../manage/clientLedgerTypes";
import { getSettlementItemKey } from "./clientLedgerCalculations";
import {
  PAYMENT_WATCHDOG_FOLLOW_UP_STATUS_OPTIONS,
  PAYMENT_WATCHDOG_PRIORITY_LABELS,
} from "./clientSettlementWatchdog";

const warningMessages = {
  missingRate: "Brak stawki dla typu lekcji.",
  missingClient: "Nie mozna przypisac ucznia do klienta.",
};

const balanceClassName = value => (
  Number(value || 0) < 0 ? "text-danger font-weight-bold" : ""
);

const getSortIcon = (sortConfig, columnKey) => {
  let icon = "▲";
  if (sortConfig.key === columnKey) {
    icon = sortConfig.direction === "ascending" ? "▲" : "▼";
  }

  return (
    <span aria-hidden="true" style={sortConfig.key === columnKey ? undefined : { visibility: "hidden" }}>
      {icon}
    </span>
  );
};

const getSortButtonLabel = (label, sortConfig, columnKey, tr) => {
  if (sortConfig.key === columnKey) {
    return tr(sortConfig.direction === 'ascending' ? 'billing:sortDescending' : 'billing:sortAscending', { label });
  }

  return tr('billing:sortAscending', { label });
};

const renderSortableHeaderLabel = (label, shortLabel) => {
  if (!shortLabel) {
    return <span>{label}</span>;
  }

  return (
    <span className="settlements-header-label-responsive">
      <span className="settlements-header-label-long">{label}</span>
      <span className="settlements-header-label-short">{shortLabel}</span>
    </span>
  );
};

const renderSortableHeader = (label, shortLabel, columnKey, className, onSort, sortConfig, tr) => (
  <div className={className}>
    <button
      type="button"
      className="btn btn-link settlements-sortable-column-button p-0 w-100"
      aria-label={getSortButtonLabel(label, sortConfig, columnKey, tr)}
      title={label}
      onClick={() => onSort(columnKey)}
    >
      <span className="text-with-icon">
        {renderSortableHeaderLabel(label, shortLabel)} {getSortIcon(sortConfig, columnKey)}
      </span>
    </button>
  </div>
);

function cancellationText(item, tr) {
  const notice = item.cancellationNoticeHours !== undefined && item.cancellationNoticeHours !== null
    ? ` (${tr('billing:noticeHours', { count: item.cancellationNoticeHours })})`
    : "";

  if (item.reason === "cancelledOnTime") {
    return `${tr('manage:attendance.cancelledOnTime')}${notice}`;
  }

  if (item.reason === "cancelledLate") {
    return `${tr('manage:attendance.cancelledLate')}${notice}`;
  }

  return "";
}

function lessonStatusText(item, tr) {
  if (item.reason === "packageConsumed") {
    return tr('manage:extraCoveredByPackage_108');
  }

  if (item.reason === "lessonCancelled") {
    return tr('manage:extraLessonCancelledByTheSchool_109');
  }

  return cancellationText(item, tr);
}

const renderLessonItem = ({ item, clientId, isExcluded, onToggle, tr, language }) => {
  const lockedByPackage = item.packageConsumed;

  return (
    <div key={getSettlementItemKey(item)} className="manage-text text-secondary d-flex align-items-center">
      <input
        type="checkbox"
        checked={!isExcluded}
        disabled={lockedByPackage}
        className="mr-2 mt-1"
        style={{ transform: "scale(1.15)" }}
        onChange={() => {
          if (!lockedByPackage) {
            onToggle(clientId, getSettlementItemKey(item));
          }
        }}
      />
      <div>
        <span className="mr-2">{displayDate(item.date, language)}</span>
        <span className="mr-2">{localizeSystemText(item.dayOfWeekName, tr) || ""}</span>
        <span className="mr-2">{item.startTime || ""}</span>
        <span className="mr-2">{item.classTypeName || item.classType || ""}</span>
        <span className="mr-2">{item.studentsPresent != null ? `${item.studentsPresent}/${item.studentsTotal}` : ""}</span>
        <span className="mr-2">{item.studentName || ""}</span>
        <span className="mr-2">{item.teacherName ? tr('billing:teacherName', { name: item.teacherName }) : ""}</span>
        <span className={item.isPresent === false ? "text-danger font-weight-bold" : ""}>
          {item.isPresent !== undefined && !item.isPresent ? tr('manage:extraAbsent_110') : ""}
        </span>
        <span className={item.reason === "autoDuplicate" ? "text-info mr-2" : "d-none"}>
          {item.reason === "autoDuplicate" ? tr('manage:extraAutomaticDuplicate_111') : ""}
        </span>
        <span>{formatLedgerAmount(item.amount, language)}</span>
        {lessonStatusText(item, tr) && (
          <div className="text-muted small">{lessonStatusText(item, tr)}</div>
        )}
      </div>
    </div>
  );
};

function renderWarning(settlement, warning, tr, language) {
  if (warning.reason === "duplicateConflict") {
    return (
      <div key={`${settlement.clientId}-${warning.duplicateKey}`} className="text-danger">
        {tr('billing:duplicateDecision', { date: displayDate(warning.date, language), time: warning.startTime || '', name: warning.studentName })}
      </div>
    );
  }

  return (
    <div key={`${settlement.clientId}-${warning.reason}-${warning.lessonRegisterId || warning.studentId || warning.classType || "warning"}`} className="text-danger">
      {localizeSystemText(warningMessages[warning.reason], tr) || warning.reason}
    </div>
  );
}

const hasWatchdogOverdueDays = watchdog => (
  watchdog.overdueDays !== undefined && watchdog.overdueDays !== null
);

const getWatchdogNoteValue = (draft, watchdog) => (
  draft.note !== undefined ? draft.note : watchdog.followUpNote
);

const renderWatchdogPanel = ({
  settlement,
  draft,
  onWatchdogDraftChange,
  onSaveWatchdogFollowUp,
  tr,
  language,
}) => {
  if (!settlement.watchdog || !settlement.watchdog.isDebtCase) {
    return null;
  }

  return (
    <div className="mb-3 p-3 border">
      <strong>{tr('billing:overdueBalanceHandling_97')} </strong>
      <div className="mt-2">
        <span className="mr-3">{tr('billing:overdueBalance_98')} {formatLedgerAmount(settlement.watchdog.amountDue, language)}</span>
        {settlement.watchdog.dueDate && <span className="mr-3">{tr('billing:dueDate_99')} {displayDate(settlement.watchdog.dueDate, language)}</span>}
        {hasWatchdogOverdueDays(settlement.watchdog) && (
          <span>{tr('billing:daysOverdue_100')} {settlement.watchdog.overdueDays}</span>
        )}
      </div>
      <div className="form-group mt-2 mb-2">
        <label htmlFor={`watchdog-status-${settlement.clientId}`}>{tr('billing:handlingStatus_101')} {settlement.clientName}</label>
        <select
          id={`watchdog-status-${settlement.clientId}`}
          className="form-control form-control-sm"
          value={draft.status || settlement.watchdog.followUpStatus}
          onChange={event => onWatchdogDraftChange(settlement, { status: event.target.value })}
        >
          {PAYMENT_WATCHDOG_FOLLOW_UP_STATUS_OPTIONS.map(option => (
            <option key={option.value} value={option.value}>{localizeSystemText(option.text, tr)}</option>
          ))}
        </select>
      </div>
      <div className="form-group mb-2">
        <label htmlFor={`watchdog-note-${settlement.clientId}`}>{tr('billing:handlingNote_102')} {settlement.clientName}</label>
        <textarea
          id={`watchdog-note-${settlement.clientId}`}
          className="form-control form-control-sm"
          rows="2"
          value={getWatchdogNoteValue(draft, settlement.watchdog)}
          onChange={event => onWatchdogDraftChange(settlement, { note: event.target.value })}
        />
      </div>
      <button type="button" className="btn btn-sm manage-button manage-red-button mr-3" onClick={() => onSaveWatchdogFollowUp(settlement)}>{tr('billing:saveHandlingDetails_103')} {settlement.clientName}
      </button>
      <small className="text-muted">
        {settlement.watchdog.followUpUpdatedAt
          ? tr('billing:lastUpdated', { date: displayDateTime(settlement.watchdog.followUpUpdatedAt, language) })
          : tr('manage:extraNoHandlingHistoryRecorded_112')}
      </small>
    </div>
  );
};

const buildDefaultExpandedState = settlements => settlements.reduce((acc, settlement) => ({
  ...acc,
  [settlement.clientId]: !settlement.approved,
}), {});

function ClientSettlementsList({
  settlements,
  sortConfig,
  expansionResetKey,
  watchdogDrafts,
  onSort,
  onToggleExclusion,
  onApprove,
  onOpenPayments,
  onWatchdogDraftChange,
  onSaveWatchdogFollowUp,
}) {
  const { t: tr, i18n } = useTranslation();
  const [expandedClientIds, setExpandedClientIds] = useState(() => buildDefaultExpandedState(settlements));
  const [expandedWatchdogIds, setExpandedWatchdogIds] = useState({});

  useEffect(() => {
    setExpandedClientIds(buildDefaultExpandedState(settlements));
    setExpandedWatchdogIds({});
  }, [expansionResetKey]);

  const toggleExpanded = clientId => {
    setExpandedClientIds(currentState => ({
      ...currentState,
      [clientId]: !currentState[clientId],
    }));
  };

  const getWatchdogDraft = settlement => {
    const key = `${settlement.clientId}#${settlement.month}`;
    return watchdogDrafts[key] || {};
  };

  return (
    <div>
      <div className="row table-header manage-table-title text-center mx-0 settlements-table-header">
        {renderSortableHeader(tr("manage:client_108"), null, "clientName", "col-md-3 table-cell settlements-sortable-column", onSort, sortConfig, tr)}
        {renderSortableHeader(tr("manage:extraOpeningBalance_113"), tr("manage:extraOpeningBal_114"), "openingBalance", "col-md-1 table-cell settlements-sortable-column", onSort, sortConfig, tr)}
        {renderSortableHeader(tr("billing:paymentsHeader"), null, "paymentsInMonth", "col-md-1 table-cell settlements-sortable-column", onSort, sortConfig, tr)}
        {renderSortableHeader(tr("billing:chargeHeader"), null, "monthlyChargeAmount", "col-md-1 table-cell settlements-sortable-column", onSort, sortConfig, tr)}
        {renderSortableHeader(tr("manage:extraClosingBalance_115"), null, "closingBalance", "col-md-2 table-cell settlements-sortable-column", onSort, sortConfig, tr)}
        <div className="col-md-1 table-cell">{tr("billing:overdueBalance_104")}</div>
        {renderSortableHeader(tr("manage:status_146"), null, "status", "col-md-1 table-cell settlements-sortable-column", onSort, sortConfig, tr)}
        <div className="col-md-2 table-cell">{tr("manage:details_149")}</div>
      </div>
      {settlements.map((settlement, index) => (
        <div key={settlement.clientId} className={index % 2 === 0 ? "" : "table-row-alternate"}>
          <div className="table-row row d-flex manage-text text-center mx-0 align-items-center">
            <div className="col-md-3 table-cell">
              {settlement.clientId ? (
                <Link to={`/manage/client/${settlement.clientId}`}>{settlement.clientName}</Link>
              ) : (
                settlement.clientName
              )}
            </div>
            <div className="col-md-1 table-cell">{settlement.billingAssignmentPending ? '—' : formatLedgerAmount(settlement.openingBalance, i18n.language)}</div>
            <div className="col-md-1 table-cell text-nowrap">
              <span className="mr-2">{formatLedgerAmount(settlement.paymentsInMonth, i18n.language)}</span>
              <button
                type="button"
                className="btn btn-link btn-sm p-0 align-baseline"
                aria-label={tr('common:managePayments', { name: settlement.clientName })}
                onClick={() => onOpenPayments(settlement.clientId, settlement.month)}
              >
                <i className="fas fa-receipt" aria-hidden="true" />
              </button>
            </div>
            <div className="col-md-1 table-cell">{formatLedgerAmount(settlement.summaryMonthlyChargeAmount, i18n.language)}</div>
            <div className={`col-md-2 table-cell ${balanceClassName(settlement.summaryClosingBalance)}`}>{settlement.billingAssignmentPending ? tr('billing:yearScope.pending') : formatLedgerAmount(settlement.summaryClosingBalance, i18n.language)}</div>
            <div className="col-md-1 table-cell">
              {settlement.watchdog && settlement.watchdog.isDebtCase ? localizeSystemText(PAYMENT_WATCHDOG_PRIORITY_LABELS[settlement.watchdog.priority], tr) : ""}
            </div>
            <div className="col-md-1 table-cell">{localizeSystemText(settlement.statusLabel, tr)}</div>
            <div className="col-md-2 table-cell text-nowrap">
              <button type="button" className="btn btn-link p-0" onClick={() => toggleExpanded(settlement.clientId)}>
                {expandedClientIds[settlement.clientId] ? tr("manage:extraHideDetails_116") : tr("manage:extraShowDetails_117")}
              </button>
            </div>
          </div>
          {expandedClientIds[settlement.clientId] && (
            <div className="mx-0 mb-4 p-3 border manage-text">
              <div className="mb-3">
                {(settlement.previewSnapshot.warnings || []).map(warning => renderWarning(settlement, warning, tr, i18n.language))}
                {(settlement.previewSnapshot.charged || []).length > 0 && (
                  <div className="mb-2">
                    <strong>{tr("billing:chargedItems_105")}</strong>
                    {(settlement.previewSnapshot.charged || []).map(item => (
                      <React.Fragment key={getSettlementItemKey(item)}>
                        {renderLessonItem({ tr, language: i18n.language, item, clientId: settlement.clientId, isExcluded: false, onToggle: onToggleExclusion })}
                      </React.Fragment>
                    ))}
                  </div>
                )}
                {(settlement.previewSnapshot.skipped || []).length > 0 && (
                  <div>
                    <strong>{tr("billing:skippedItems_106")}</strong>
                    {(settlement.previewSnapshot.skipped || []).map(item => renderLessonItem({ tr,
                      language: i18n.language,
                      item,
                      clientId: settlement.clientId,
                      isExcluded: true,
                      onToggle: onToggleExclusion,
                    }))}
                  </div>
                )}
              </div>
              <div className="mt-3 mb-3">
                {(settlement.otherYears || []).map(item => (
                  <div key={item.schoolYearStartYear}>
                    {tr('billing:yearScope.balance')} {item.schoolYearStartYear}/{String(item.schoolYearStartYear + 1).slice(-2)}: {formatLedgerAmount(item.balance, i18n.language)}
                  </div>
                ))}
                <div>{tr("billing:draftTotal_107")} {formatLedgerAmount(settlement.previewMonthlyChargeAmount, i18n.language)}</div>
                <div>{tr("billing:balanceAfterRecalculation_108")} {settlement.billingAssignmentPending ? '—' : formatLedgerAmount(settlement.previewClosingBalance, i18n.language)}</div>
              </div>
              <div id={`watchdog-panel-${settlement.clientId}`} hidden={!expandedWatchdogIds[settlement.clientId]}>
                {renderWatchdogPanel({ tr,
                  language: i18n.language,
                  settlement,
                  draft: getWatchdogDraft(settlement),
                  onWatchdogDraftChange,
                  onSaveWatchdogFollowUp,
                })}
              </div>
              <button type="button" className="btn btn-sm manage-button manage-red-button" onClick={() => onApprove(settlement.previewSettlement)}>
                {settlement.approved ? tr("manage:extraRecalculateAndSaveAgain_118") : tr("billing:approveAction")}
              </button>
              {settlement.watchdog && settlement.watchdog.isDebtCase && (
                <button
                  type="button"
                  className="btn btn-sm manage-button manage-teal-button ml-2"
                  aria-expanded={!!expandedWatchdogIds[settlement.clientId]}
                  aria-controls={`watchdog-panel-${settlement.clientId}`}
                  onClick={() => setExpandedWatchdogIds(current => ({ ...current, [settlement.clientId]: !current[settlement.clientId] }))}
                >
                  {tr('billing:overdueBalanceHandling_97')}
                </button>
              )}
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

ClientSettlementsList.propTypes = {
  settlements: PropTypes.array.isRequired,
  sortConfig: PropTypes.shape({
    key: PropTypes.string.isRequired,
    direction: PropTypes.string.isRequired,
  }).isRequired,
  expansionResetKey: PropTypes.string.isRequired,
  watchdogDrafts: PropTypes.object,
  onSort: PropTypes.func.isRequired,
  onToggleExclusion: PropTypes.func.isRequired,
  onApprove: PropTypes.func.isRequired,
  onOpenPayments: PropTypes.func.isRequired,
  onWatchdogDraftChange: PropTypes.func,
  onSaveWatchdogFollowUp: PropTypes.func,
};

ClientSettlementsList.defaultProps = {
  watchdogDrafts: {},
  onWatchdogDraftChange: () => {},
  onSaveWatchdogFollowUp: () => {},
};

export default ClientSettlementsList;
